import { cn } from '@/shared/lib';
import { FC, useMemo } from 'react';
import { Input } from '@/shared/shadcn';
import { useTranslation } from 'react-i18next';
import { BasketDeliveryProps } from '../BasketDelivery';
import { ServiceTypeEnum } from '@/app/store/api/restApi';
import { InformationNote, InputErrorMessage } from '@/shared/components';
import { useAppSelector, useAppDispatch, openDeliveryAddressModal, selectIsMinimalistic } from '@/app/store';
import {
  AddressView,
  BasketInputs,
  DeliverySuggester,
  BasketOptionSwitcher,
  useDeliveryChangePersonalInfo,
} from '@/features';

interface BasketDeliveryPersonalInfoProps extends BasketDeliveryProps {
  isOrderAnotherPerson?: boolean;
}

export const BasketDeliveryPersonalInfo: FC<BasketDeliveryPersonalInfoProps> = ({
  showSelector,
  setShowSelector,
  isOrderAnotherPerson = false,
}) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation('basket');
  const { t: tAddress } = useTranslation('address');
  const { deliveryError } = useAppSelector((state) => state.errors);
  const { delivery: myDelivery, options } = useAppSelector((state) => state.order);
  const { delivery: anotherDelivery, leaveAtTheDoor } = useAppSelector((state) => state.orderAnotherPerson);
  const { deliveryDisabled, orderPointDisabled } = useAppSelector((state) => state.application);
  const { leaveOrderAtDoorOptionEnabled, commentsEnabled } = useAppSelector((state) => state.initial.settings);
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const delivery = useMemo(
    () => (isOrderAnotherPerson ? anotherDelivery : myDelivery),
    [anotherDelivery, isOrderAnotherPerson, myDelivery],
  );

  const leaveAtTheDoorValue = useMemo(
    () => (isOrderAnotherPerson ? leaveAtTheDoor : options.leaveAtTheDoor),
    [isOrderAnotherPerson, leaveAtTheDoor, options.leaveAtTheDoor],
  );

  const { onSetFloor, onSetIntercom, onSetApartement, onSetEntranceway, onChangeLeaveAtTheDoor } =
    useDeliveryChangePersonalInfo(isOrderAnotherPerson);

  return (
    <div className={cn('flex flex-col', isMinimalistic ? 'gap-2' : 'gap-3')}>
      <div>
        {showSelector ? (
          <DeliverySuggester
            resetManualCoordinates
            onSuccess={() => setShowSelector(false)}
            isOrderAnotherPerson={isOrderAnotherPerson}
            inputClassName={deliveryError && 'border-destructive'}
          />
        ) : (
          <AddressView
            error={!!deliveryError}
            onClick={() => {
              if (orderPointDisabled) setShowSelector(true);
              else dispatch(openDeliveryAddressModal(isOrderAnotherPerson));
            }}
            onClickMap={() => dispatch(openDeliveryAddressModal(isOrderAnotherPerson))}
            value={
              delivery.areManualCoordinates && delivery.coords
                ? `${delivery.address ? delivery.address + ', ' : ''}${delivery.coords.lat.toFixed(5)}, ${delivery.coords.lng.toFixed(5)}`
                : delivery.address
            }
            deliveryType={ServiceTypeEnum.Delivery}
            onlyDelivery={!deliveryDisabled && orderPointDisabled}
            onlyOrderPoint={deliveryDisabled && !orderPointDisabled}
          />
        )}

        {deliveryError && <InputErrorMessage text={deliveryError} />}
      </div>

      <div className='flex gap-2'>
        <Input
          maxLength={20}
          placeholder={tAddress('placeholders.entrance')}
          value={delivery.entranceway}
          onChange={(e) => onSetEntranceway(e.target.value)}
        />
        <Input
          maxLength={5}
          placeholder={tAddress('placeholders.floor')}
          value={delivery.floor}
          onChange={(e) => onSetFloor(e.target.value)}
        />
      </div>

      <div className={cn('flex gap-2')}>
        <Input
          maxLength={5}
          value={delivery.apartment}
          placeholder={tAddress('placeholders.apartment')}
          onChange={(e) => onSetApartement(e.target.value)}
        />
        <Input
          maxLength={20}
          placeholder={tAddress('placeholders.intercom')}
          value={delivery.intercom}
          onChange={(e) => onSetIntercom(e.target.value)}
        />
      </div>

      <BasketInputs commentsEnabled={commentsEnabled} isOrderAnotherPerson={isOrderAnotherPerson} />

      {delivery.nationwideInfo ? (
        <InformationNote title={t('nationwide.restaurantComment')} description={t('nationwide.courierDescription')} />
      ) : (
        leaveOrderAtDoorOptionEnabled && (
          <BasketOptionSwitcher
            checked={leaveAtTheDoorValue}
            onCheckedChange={(val: boolean) => onChangeLeaveAtTheDoor(val)}
          />
        )
      )}
    </div>
  );
};
